import React, {
  useCallback, useEffect, useMemo,
} from 'react';

import PropTypes from 'prop-types';
import { TextField, Select } from '@picsio/ui';

import l10n from '../../../../shared/strings';
import {
  PRODUCT_DEFAULT_UNITS,
  PRODUCT_UNITS,
  getUnitDisplayLabel,
} from '../../helpers';
import { useBlurCommit, useEnterToBlur, useLocalFieldValue } from '../../hooks';
import {
  normalizeMeasurementValue,
  serializeMeasurementValue,
} from '../utils';
import ProductFieldRow from './ProductFieldRow';
import ProductFieldLabel from './ProductFieldLabel';

function buildUnitSelectOptions(type) {
  return (PRODUCT_UNITS[type] || []).map((unit) => ({
    value: unit,
    title: getUnitDisplayLabel(type, unit),
  }));
}

const MeasurementField = ({
  data, value, disabled, isDiff, onChange, validate, savedError, onValidationChange,
}) => {
  const unitOptions = useMemo(
    () => buildUnitSelectOptions(data.type),
    [data.type],
  );
  const defaultUnit = data.unit || PRODUCT_DEFAULT_UNITS[data.type] || unitOptions[0]?.value || '';
  const normalized = useMemo(
    () => normalizeMeasurementValue(value, defaultUnit),
    [defaultUnit, value],
  );
  const { localValue, setLocalValue } = useLocalFieldValue(normalized);

  useEffect(() => {
    setLocalValue(normalizeMeasurementValue(value, defaultUnit));
  }, [defaultUnit, setLocalValue, value]);

  const commit = useCallback((nextValue, onCancel) => {
    onChange(serializeMeasurementValue(nextValue), data, onCancel);
  }, [data, onChange]);

  const { handleBlur: handleValueBlur, error: validationError, clearErrorIfValid } = useBlurCommit({
    value: normalized,
    localValue,
    setLocalValue,
    onCommit: commit,
    serialize: serializeMeasurementValue,
    validate,
    savedError,
    onValidationChange,
  });

  const renderUnitValue = useCallback((unitId) => {
    const option = unitOptions.find((o) => o.value === unitId);
    return option?.title ?? getUnitDisplayLabel(data.type, unitId);
  }, [data.type, unitOptions]);

  const handleUnitChange = useCallback((event) => {
    const nextValue = {
      ...localValue,
      unit: event.target.value,
    };
    setLocalValue(nextValue);
    clearErrorIfValid(nextValue);
    commit(nextValue, () => setLocalValue(normalized));
  }, [clearErrorIfValid, commit, localValue, normalized, setLocalValue]);

  const handleValueChange = useCallback((event) => {
    const nextValue = { ...localValue, value: event.target.value };
    setLocalValue(nextValue);
    clearErrorIfValid(nextValue);
  }, [clearErrorIfValid, localValue, setLocalValue]);

  const handleKeyPress = useEnterToBlur();

  return (
    <ProductFieldRow title={data.title}>
      <TextField
        value={localValue.value}
        label={(
          <ProductFieldLabel
            title={data.title}
            tooltip={l10n.PRODUCT_INFO.fieldHelpers[data.type]}
            isDiff={isDiff}
          />
        )}
        disabled={disabled}
        type="number"
        error={Boolean(validationError)}
        helperText={validationError}
        InputProps={{ step: 'any' }}
        onChange={handleValueChange}
        onBlur={handleValueBlur}
        onKeyPress={handleKeyPress}
      />
      <If condition={unitOptions.length}>
        <Select
          disabled={disabled}
          options={unitOptions}
          dataId="value"
          dataValue="title"
          value={localValue.unit}
          renderValue={renderUnitValue}
          onChange={handleUnitChange}
        />
      </If>
    </ProductFieldRow>
  );
};

MeasurementField.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    type: PropTypes.string,
    unit: PropTypes.string,
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({})]),
  disabled: PropTypes.bool,
  isDiff: PropTypes.bool,
  validate: PropTypes.func,
  savedError: PropTypes.string,
  onValidationChange: PropTypes.func,
  onChange: PropTypes.func.isRequired,
};

MeasurementField.defaultProps = {
  value: '',
  disabled: false,
  isDiff: false,
  validate: null,
  savedError: null,
  onValidationChange: null,
};

export default MeasurementField;
